import promotionApi from '@/apis/manufacturerPromotions';

const defaultList = () => ({
    items: [],
    total: 0,
    page: 1,
    size: 20,
});

export default {
    namespaced: true,
    state: () => ({
        promotions: defaultList(),
        entries: defaultList(),
        messageLogs: defaultList(),
        couponSlots: defaultList(),
        currentPromotion: null,
        loading: false,
        detailLoading: false,
        submitting: false,
    }),
    mutations: {
        SET_LOADING(state, value) { state.loading = value; },
        SET_DETAIL_LOADING(state, value) { state.detailLoading = value; },
        SET_SUBMITTING(state, value) { state.submitting = value; },
        SET_PROMOTIONS(state, payload) {
            state.promotions = {
                items: payload.items || [],
                total: payload.total || 0,
                page: payload.page || 1,
                size: payload.size || 20,
            };
        },
        SET_CURRENT_PROMOTION(state, promotion) { state.currentPromotion = promotion || null; },
        SET_ENTRIES(state, payload) {
            state.entries = {
                items: payload.items || [],
                total: payload.total || 0,
                page: payload.page || 1,
                size: payload.size || 20,
            };
        },
        SET_MESSAGE_LOGS(state, payload) {
            state.messageLogs = {
                items: payload.items || [],
                total: payload.total || 0,
                page: payload.page || 1,
                size: payload.size || 20,
            };
        },
        SET_COUPON_SLOTS(state, payload) {
            state.couponSlots = {
                items: payload.items || [],
                total: payload.total || 0,
                page: payload.page || 1,
                size: payload.size || 20,
            };
        },
    },
    actions: {
        async fetchPromotions({ commit }, params = {}) {
            commit('SET_LOADING', true);
            try {
                const response = await promotionApi.listPromotions(params);
                const payload = response?.data?.data || {};
                commit('SET_PROMOTIONS', payload);
                return payload;
            } finally {
                commit('SET_LOADING', false);
            }
        },
        async createPromotion({ commit }, payload) {
            commit('SET_SUBMITTING', true);
            try {
                const response = await promotionApi.createPromotion(payload);
                return response?.data?.data || null;
            } finally {
                commit('SET_SUBMITTING', false);
            }
        },
        async checkPublicSlugAvailability(_ctx, params = {}) {
            const response = await promotionApi.checkPublicSlugAvailability(params);
            return response?.data?.data || null;
        },
        async uploadPromotionImage(_ctx, { file, maxFileSize = 10 }) {
            const response = await promotionApi.uploadImage(file, maxFileSize);
            return response?.data?.url || '';
        },
        async fetchPromotion({ commit }, promotionId) {
            commit('SET_DETAIL_LOADING', true);
            try {
                const response = await promotionApi.getPromotion(promotionId);
                const promotion = response?.data?.data || null;
                commit('SET_CURRENT_PROMOTION', promotion);
                return promotion;
            } finally {
                commit('SET_DETAIL_LOADING', false);
            }
        },
        async updatePromotion({ commit }, { promotionId, payload }) {
            commit('SET_SUBMITTING', true);
            try {
                const response = await promotionApi.updatePromotion(promotionId, payload);
                const promotion = response?.data?.data || null;
                commit('SET_CURRENT_PROMOTION', promotion);
                return promotion;
            } finally {
                commit('SET_SUBMITTING', false);
            }
        },
        async activatePromotion({ commit }, promotionId) {
            const response = await promotionApi.activatePromotion(promotionId);
            const promotion = response?.data?.data || null;
            commit('SET_CURRENT_PROMOTION', promotion);
            return promotion;
        },
        async pausePromotion({ commit }, promotionId) {
            const response = await promotionApi.pausePromotion(promotionId);
            const promotion = response?.data?.data || null;
            commit('SET_CURRENT_PROMOTION', promotion);
            return promotion;
        },
        async closePromotion({ commit }, promotionId) {
            const response = await promotionApi.closePromotion(promotionId);
            const promotion = response?.data?.data || null;
            commit('SET_CURRENT_PROMOTION', promotion);
            return promotion;
        },
        async deletePromotion({ commit, state }, promotionId) {
            const response = await promotionApi.deletePromotion(promotionId);
            if ((state.currentPromotion?._id || state.currentPromotion?.id) === promotionId) {
                commit('SET_CURRENT_PROMOTION', null);
            }
            return response?.data?.data || null;
        },
        async fetchOperationalData(
            { commit },
            {
                promotionId,
                page = 1,
                size = 20,
                keyword = '',
                searchField = 'all',
                status = '',
            } = {},
        ) {
            const [entries, messageLogs, couponSlots] = await Promise.all([
                promotionApi.listEntries(promotionId, {
                    page,
                    size,
                    keyword,
                    searchField,
                    status,
                }),
                promotionApi.listMessageLogs(promotionId, { page, size }),
                promotionApi.listCouponSlots(promotionId, { page, size }),
            ]);
            commit('SET_ENTRIES', entries?.data?.data || {});
            commit('SET_MESSAGE_LOGS', messageLogs?.data?.data || {});
            commit('SET_COUPON_SLOTS', couponSlots?.data?.data || {});
        },
        async fetchEntries(
            { commit },
            {
                promotionId,
                page = 1,
                size = 20,
                keyword = '',
                searchField = 'all',
                status = '',
            } = {},
        ) {
            const response = await promotionApi.listEntries(promotionId, {
                page,
                size,
                keyword,
                searchField,
                status,
            });
            const payload = response?.data?.data || {};
            commit('SET_ENTRIES', payload);
            return payload;
        },
        async fetchEntry(_ctx, { promotionId, entryId }) {
            const response = await promotionApi.getEntry(promotionId, entryId);
            return response?.data?.data || null;
        },
        async exportEntries(_ctx, { promotionId, payload = {} }) {
            return promotionApi.exportEntries(promotionId, payload);
        },
        async sendTestMessage(_ctx, { promotionId, payload }) {
            const response = await promotionApi.sendTestMessage(promotionId, payload);
            return response?.data?.data || null;
        },
        async resendMessageLog(_ctx, { promotionId, logId }) {
            const response = await promotionApi.resendMessageLog(promotionId, logId);
            return response?.data?.data || null;
        },
        async fetchMessageLogs(_ctx, { promotionId, params = {} }) {
            const response = await promotionApi.listMessageLogs(promotionId, params);
            return response?.data?.data || {};
        },
        async deleteEntry(_ctx, { promotionId, entryId }) {
            const response = await promotionApi.deleteEntry(promotionId, entryId);
            return response?.data?.data || null;
        },
        async deleteEntries(_ctx, { promotionId, entryIds = [] }) {
            const response = await promotionApi.deleteEntries(promotionId, entryIds);
            return response?.data?.data || null;
        },
    },
    getters: {
        promotions: (state) => state.promotions.items,
        currentPromotion: (state) => state.currentPromotion,
        entries: (state) => state.entries.items,
        messageLogs: (state) => state.messageLogs.items,
        couponSlots: (state) => state.couponSlots.items,
    },
};
